import React, { useEffect } from 'react'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'
import Select from '@vtex/styleguide/lib/EXPERIMENTAL_Select'

import { SLA_SELECTOR_TYPES, PossibleSlaTypes } from '../../constants'
import * as CheckoutConstants from '@/checkout-instore-legacy/constants/CheckoutConstants'
import pgUtils from '@/checkout-instore-legacy/utils/PromiseGroupUtils'

const createNewFlags = (type: string) => ({
  isCheapestSelected: type === SLA_SELECTOR_TYPES.cheapest,
  isFastestSelected: type === SLA_SELECTOR_TYPES.fastest,
  isScheduledDeliverySelected: type === SLA_SELECTOR_TYPES.scheduled,
  isPickupSelected: type === SLA_SELECTOR_TYPES.pickup,
  isCustomSelected: type === SLA_SELECTOR_TYPES.custom,
})

const possibleNewFlags = {
  [SLA_SELECTOR_TYPES.cheapest]: createNewFlags(SLA_SELECTOR_TYPES.cheapest),
  [SLA_SELECTOR_TYPES.fastest]: createNewFlags(SLA_SELECTOR_TYPES.fastest),
  [SLA_SELECTOR_TYPES.scheduled]: createNewFlags(SLA_SELECTOR_TYPES.scheduled),
  [SLA_SELECTOR_TYPES.pickup]: createNewFlags(SLA_SELECTOR_TYPES.pickup),
  [SLA_SELECTOR_TYPES.custom]: createNewFlags(SLA_SELECTOR_TYPES.custom),
}

export type Flags = {
  shouldCheapestExist?: boolean
  shouldFastestExist?: boolean
  shouldScheduledDeliveryExist?: boolean
  shouldPickupExist?: boolean
  shouldCustomExist?: boolean
  isPickupOnlyLogisticsInfo?: boolean
  isCheapestSelected?: boolean
  isFastestSelected?: boolean
  isPickupSelected?: boolean
  isScheduledDeliverySelected?: boolean
  isCustomSelected?: boolean
}

type ChooseSelectorSla = {
  flags: Flags
  onSelectorTypeChanged: (flags: Partial<Flags>, type: PossibleSlaTypes) => void
}

const ChooseSelectorType = ({
  flags,
  onSelectorTypeChanged,
}: ChooseSelectorSla) => {
  const {
    shouldCheapestExist,
    shouldFastestExist,
    shouldScheduledDeliveryExist,
    shouldPickupExist,
    shouldCustomExist,
    isCheapestSelected,
    isPickupOnlyLogisticsInfo,
    isFastestSelected,
    isScheduledDeliverySelected,
    isPickupSelected,
    isCustomSelected,
  } = flags

  const intl = useIntl()

  useEffect(() => {
    if (isPickupOnlyLogisticsInfo && !isPickupSelected) {
      // When isCheckedIn: false and the orderForm only has pickup points, the Checkout API don't auto select pickup points, so this will fix it (Check pickup_only_spec if you will change this)
      pgUtils
        .waitFor(CheckoutConstants.CHECKOUT_GROUPS.saveShippingData) // Wait for any shipping request to finish first so there is no race condition
        .then(() => {
          onSelectorTypeChanged(
            possibleNewFlags[SLA_SELECTOR_TYPES.pickup],
            SLA_SELECTOR_TYPES.pickup
          )
        })
    }
  }, [isPickupOnlyLogisticsInfo, isPickupSelected, onSelectorTypeChanged])

  const slaSelectorTypes = [
    {
      type: SLA_SELECTOR_TYPES.cheapest,
      selected: isCheapestSelected,
      exists: shouldCheapestExist,
      messageId: 'shippingSlaSelectorCheaper',
    },
    {
      type: SLA_SELECTOR_TYPES.fastest,
      selected: isFastestSelected,
      exists: shouldFastestExist,
      messageId: 'shippingSlaSelectorFaster',
    },
    {
      type: SLA_SELECTOR_TYPES.scheduled,
      selected: isScheduledDeliverySelected,
      exists: shouldScheduledDeliveryExist,
      messageId: 'shippingSlaSelectorScheduled',
    },
    {
      type: SLA_SELECTOR_TYPES.pickup,
      selected: isPickupSelected,
      exists: shouldPickupExist,
      messageId: 'shippingSlaSelectorPickup',
    },
    {
      type: SLA_SELECTOR_TYPES.custom,
      selected: isCustomSelected,
      exists: shouldCustomExist,
      messageId: 'shippingSlaSelectorCustom',
    },
  ]

  const options = slaSelectorTypes
    .filter((sla) => sla.exists)
    .map((sla) => ({
      ...sla,
      label: intl.formatMessage({ id: sla.messageId }),
      value: sla.type,
      id: `select-${sla.type}-slas`,
    }))

  const selectedSla = options.find((sla) => sla.selected)

  const handleOnChangeSelector = (type: PossibleSlaTypes) => {
    const flags = possibleNewFlags[type]

    return onSelectorTypeChanged(flags, type)
  }

  return (
    <div className="flex">
      <div data-testid="sla-select-type" className="w-50-ns w-100-s">
        <Select
          options={options}
          multi={false}
          clearable={false}
          searchable={false}
          size="large"
          value={selectedSla}
          onChange={({ type }: { type: PossibleSlaTypes }) => {
            handleOnChangeSelector(type)
          }}
        />
      </div>
    </div>
  )
}

export default ChooseSelectorType
